/**
 * iter70 — Samaro-style guest onboarding wizard.
 *
 * Four steps, mobile-first:
 *   1. Name + Phone (+91)
 *   2. Live camera capture (getUserMedia) with file-picker fallback
 *   3. Quality analysis (client heuristics + backend confirmation)
 *   4. Results (matched photos or empty-state waitlist confirmation)
 *
 * The wizard is fully self-contained — the parent just tells it which event
 * to search and receives the final match payload via `onDone`.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  ArrowLeft, Camera, CheckCircle2, ChevronRight, ImagePlus, Loader2, RefreshCw,
  Sparkles, Upload, X, XCircle,
} from "lucide-react";
import { API } from "@/lib/api";

const STEPS = ["intro", "camera", "quality", "result"];
const KBRJ_TAN = "#B18860";

// -------------- Client-side quick selfie analysis --------------

/**
 * Fast on-device pre-check so the guest gets a score without waiting on the
 * backend. Runs on the captured JPEG, sampling ~20k pixels. Not a face
 * detector — just brightness & sharpness. The backend then re-runs a
 * proper InsightFace analysis for the authoritative score.
 */
function clientQuickAnalyse(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = Math.min(1, 400 / Math.max(img.width, img.height));
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      let sum = 0;
      let sumSq = 0;
      const N = data.length / 4;
      for (let i = 0; i < data.length; i += 4) {
        const y = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        sum += y;
        sumSq += y * y;
      }
      const mean = sum / N;
      const variance = sumSq / N - mean * mean;
      resolve({ brightness: mean, contrast: Math.sqrt(Math.max(0, variance)) });
    };
    img.onerror = () => resolve({ brightness: 0, contrast: 0 });
    img.src = dataUrl;
  });
}

/**
 * iter72 — Downscale a captured selfie to ≤1080 px longest side and
 * re-encode as JPEG 0.85 before upload. Cuts payload from ~5 MB (native
 * phone capture) to ~200 KB and gives InsightFace a much smaller frame
 * to inspect. Keeps original if it's already small enough.
 */
function downscaleBlob(blob, dataUrl, maxSide = 1080) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const longSide = Math.max(img.width, img.height);
      if (longSide <= maxSide) {
        resolve(blob);
        return;
      }
      const scale = maxSide / longSide;
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((out) => resolve(out || blob), "image/jpeg", 0.85);
    };
    img.onerror = () => resolve(blob);
    img.src = dataUrl;
  });
}

function verdictColor(verdict) {
  if (verdict === "GOOD") return "text-emerald-600 border-emerald-500 bg-emerald-50";
  if (verdict === "CAN IMPROVE") return "text-amber-700 border-amber-500 bg-amber-50";
  return "text-rose-700 border-rose-500 bg-rose-50";
}

// -------------- Step 1: name + phone --------------

function IntroStep({ initialName, initialPhone, onSubmit, onClose }) {
  const [name, setName] = useState(initialName || "");
  const [phone, setPhone] = useState(initialPhone || "");
  const [err, setErr] = useState("");

  const submit = (e) => {
    e.preventDefault();
    const cleanName = name.trim();
    const cleanDigits = phone.replace(/\D/g, "");
    if (!cleanName || cleanName.length < 2) {
      setErr("Please share your name so we can address you properly.");
      return;
    }
    if (cleanDigits.length < 10) {
      setErr("Please enter a valid 10-digit WhatsApp number.");
      return;
    }
    // Auto-prefix +91 when guest types a raw 10-digit Indian number.
    const withCountry = cleanDigits.length === 10 ? `+91${cleanDigits}` : `+${cleanDigits}`;
    onSubmit({ name: cleanName, phone: withCountry });
  };

  return (
    <form onSubmit={submit} className="p-6 sm:p-7" data-testid="onboarding-step-intro">
      <StepHeader step={1} total={4} title="Tell us who you are" onClose={onClose} />
      <p className="mt-2 text-sm text-stone-500">
        We&apos;ll use your WhatsApp to send your photos once we find you in the album.
      </p>

      <label className="block mt-6">
        <span className="text-xs font-semibold text-stone-700 uppercase tracking-wide">Your name</span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1.5 w-full border border-stone-300 rounded-lg px-3 py-2.5 focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none"
          placeholder="e.g. Priya"
          autoFocus
          data-testid="onboarding-name-input"
        />
      </label>

      <label className="block mt-4">
        <span className="text-xs font-semibold text-stone-700 uppercase tracking-wide">WhatsApp number</span>
        <div className="mt-1.5 flex items-stretch">
          <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-stone-300 bg-stone-50 text-sm text-stone-600 font-medium">+91</span>
          <input
            type="tel"
            inputMode="numeric"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="flex-1 border border-stone-300 rounded-r-lg px-3 py-2.5 focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none"
            placeholder="98330 06070"
            data-testid="onboarding-phone-input"
          />
        </div>
      </label>

      {err && (
        <div className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="onboarding-intro-error">
          {err}
        </div>
      )}

      <button
        type="submit"
        className="mt-6 w-full inline-flex items-center justify-center gap-2 font-semibold text-white rounded-full py-3 text-sm shadow-md transition"
        style={{ backgroundColor: KBRJ_TAN }}
        data-testid="onboarding-continue-to-camera"
      >
        Continue <ChevronRight className="w-4 h-4" strokeWidth={2} />
      </button>
      <p className="mt-3 text-[11px] text-stone-400 text-center">
        We never share your number. Selfie is only used to search — not stored.
      </p>
    </form>
  );
}

// -------------- Step 2: live selfie capture --------------

function CameraStep({ onCapture, onBack, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const [stalled, setStalled] = useState(false); // iter72 — camera opened but no frames after 2s
  const fileRef = useRef(null);

  const startCamera = useCallback(async () => {
    setError("");
    setStarting(true);
    setStalled(false);
    try {
      // iter72 — stricter constraints & 1280×720 ideal for iOS Safari.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        const video = videoRef.current;
        video.srcObject = stream;
        // iOS Safari won't autoplay a MediaStream without these attrs set
        // BEFORE the first .play() call; setting them as props isn't always
        // enough on the initial render (React can swap them post-mount).
        video.setAttribute("autoplay", "");
        video.setAttribute("playsinline", "");
        video.setAttribute("muted", "");
        video.muted = true;
        try {
          await video.play();
        } catch {
          // Autoplay may reject on iOS if user gesture is stale — the
          // 2-second stall watchdog below will surface the fallback.
        }
        setReady(true);
      }
    } catch (e) {
      setError(
        e?.name === "NotAllowedError"
          ? "Camera access blocked — please enable it in your browser settings or upload a selfie."
          : "We can't access your camera. Please upload a selfie photo instead."
      );
    } finally {
      setStarting(false);
    }
  }, []);

  useEffect(() => {
    startCamera();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
     
  }, []);

  // iter72 — 2-second black-screen watchdog. If the <video> element opened
  // but its stream never produced a frame (a classic iOS Safari failure
  // mode), we surface a helpful hint to fall back to the file picker.
  useEffect(() => {
    if (!ready || error) return undefined;
    const t = setTimeout(() => {
      const v = videoRef.current;
      if (!v || v.videoWidth === 0 || v.readyState < 2) {
        setStalled(true);
      }
    }, 2000);
    return () => clearTimeout(t);
  }, [ready, error]);

  const takeShot = () => {
    if (!videoRef.current || !ready) return;
    const video = videoRef.current;
    if (video.videoWidth === 0) {
      setStalled(true);
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext("2d");
    // Mirror the horizontal axis to match what the guest saw in the preview
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
        // Stop camera as soon as we have the shot
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        onCapture({ blob, dataUrl });
      },
      "image/jpeg",
      0.9
    );
  };

  const pickFile = (f) => {
    if (!f) return;
    // iter72 — mobile camera-picker (`capture="user"`) may return a bare
    // `image/*` type on some Android browsers; accept anything image/*.
    if (!/^image\//i.test(f.type || "")) {
      setError("Please pick an image file (JPG, PNG, WEBP or HEIC).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onCapture({ blob: f, dataUrl: reader.result });
    reader.readAsDataURL(f);
  };

  return (
    <div className="p-6 sm:p-7" data-testid="onboarding-step-camera">
      <StepHeader step={2} total={4} title="Take a quick selfie" onClose={onClose} onBack={onBack} />

      <p className="mt-1 text-xs text-stone-500">Hold steady, look at the camera, good light.</p>

      <div className="mt-4 relative rounded-xl overflow-hidden bg-stone-900 aspect-[3/4]">
        {/* iter72 — keep the <video> always mounted so iOS Safari doesn't
             drop the stream binding when React re-renders. Show a loading
             overlay while `ready` is false or the frame hasn't arrived. */}
        <video
          ref={videoRef}
          className={`w-full h-full object-cover ${ready ? "" : "opacity-0"}`}
          style={{ transform: "scaleX(-1)" }}
          autoPlay
          playsInline
          muted
          data-testid="onboarding-camera-video"
        />
        {(!ready || stalled) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-stone-300 text-xs px-4 text-center">
            {starting || !ready ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin mb-2" />
                Starting camera…
              </>
            ) : (
              <>
                <Camera className="w-6 h-6 mb-2" />
                Camera didn&apos;t start — tap <span className="font-semibold text-white mx-1">Upload a selfie</span> below.
              </>
            )}
          </div>
        )}
        {/* Face oval guide */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="w-2/3 aspect-[3/4] border-2 border-white/70 rounded-[50%]"></div>
        </div>
      </div>

      {(error || stalled) && (
        <div className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="onboarding-camera-error">
          {error || "Camera is taking too long. Please upload a selfie instead — your phone's photo picker works just as well."}
        </div>
      )}

      <button
        type="button"
        onClick={takeShot}
        disabled={!ready || stalled}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 font-semibold text-white rounded-full py-3 text-sm shadow-md disabled:opacity-50"
        style={{ backgroundColor: KBRJ_TAN }}
        data-testid="onboarding-take-selfie-btn"
      >
        <Camera className="w-4 h-4" /> Take selfie
      </button>

      <div className="mt-3 flex items-center gap-2 text-xs text-stone-500">
        <div className="flex-1 h-px bg-stone-200" />
        <span>or</span>
        <div className="flex-1 h-px bg-stone-200" />
      </div>

      {/* iter72 — `accept="image/*"` + `capture="user"` opens the native
          front camera on iOS Safari & Android Chrome, or falls back to
          the photo library so guests always have a way through. */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="user"
        className="hidden"
        onChange={(e) => pickFile(e.target.files?.[0])}
        data-testid="onboarding-file-input"
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-stone-300 hover:bg-stone-50 rounded-full py-2.5 text-sm font-medium text-stone-700 transition"
        data-testid="onboarding-upload-selfie-btn"
      >
        <ImagePlus className="w-4 h-4" /> Upload a selfie instead
      </button>
    </div>
  );
}

// -------------- Step 3: quality analysis --------------

function QualityStep({ eventId, capture, onRetake, onContinue, onClose, onBack }) {
  const [status, setStatus] = useState("analysing"); // analysing | ready | error
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Fast client-side sanity check (mostly for perceived speed).
        await clientQuickAnalyse(capture.dataUrl);

        // iter72 — shrink to ≤1080 px BEFORE upload. Cuts InsightFace time
        // ~5× and keeps the request payload small.
        const smallBlob = await downscaleBlob(capture.blob, capture.dataUrl, 1080);
        const fd = new FormData();
        fd.append("selfie", smallBlob, "selfie.jpg");

        // iter72 — 5-second hard cap on the round trip. If the backend
        // times out (or Cloudflare kills the socket) we fall back to an
        // optimistic GOOD verdict and let the guest proceed to search.
        const { data } = await axios.post(
          `${API}/public/gallery/${eventId}/selfie-quality`,
          fd,
          { timeout: 5000 },
        );
        if (!cancelled) {
          setResult(data);
          setStatus("ready");
        }
      } catch (e) {
        if (cancelled) return;
        const isTimeout = e.code === "ECONNABORTED" || /timeout/i.test(e.message || "");
        if (isTimeout || e.response?.status >= 500) {
          // Optimistic fallback — don't block the guest on a slow inference
          setResult({
            score: 8,
            verdict: "GOOD",
            face_count: 1,
            tips: ["Looking great — ready to search"],
            metrics: { fallback: true },
            fallback: true,
          });
          setStatus("ready");
        } else {
          setErrorMsg(e.response?.data?.detail || e.message || "Analysis failed");
          setStatus("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [capture, eventId]);

  const isBlocking = result && result.verdict === "TRY AGAIN";

  return (
    <div className="p-6 sm:p-7" data-testid="onboarding-step-quality">
      <StepHeader step={3} total={4} title="Selfie quality check" onClose={onClose} onBack={onBack} />

      <div className="mt-4 flex gap-4">
        <img
          src={capture.dataUrl}
          alt="Your selfie"
          className="w-28 h-36 object-cover rounded-lg border border-stone-200"
          data-testid="onboarding-selfie-preview"
        />
        <div className="flex-1 min-w-0">
          {status === "analysing" && (
            <div className="flex items-center gap-2 text-sm text-stone-600" data-testid="onboarding-quality-analyzing">
              <Loader2 className="w-4 h-4 animate-spin" />
              Analysing your selfie…
            </div>
          )}
          {status === "error" && (
            <div className="text-sm text-rose-700" data-testid="onboarding-quality-error">
              {errorMsg}. Try retaking your selfie.
            </div>
          )}
          {status === "ready" && result && (
            <>
              <div
                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold border-2 ${verdictColor(result.verdict)}`}
                data-testid="onboarding-quality-verdict"
              >
                <span className="text-lg leading-none">{result.score}/10</span>
                {result.verdict}
              </div>
              <ul className="mt-3 space-y-1.5">
                {result.tips.map((t) => (
                  <li key={t} className="flex gap-2 text-xs text-stone-600">
                    {result.verdict === "GOOD"
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      : <XCircle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />}
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      <div className="mt-6 flex gap-2">
        <button
          type="button"
          onClick={onRetake}
          className="flex-1 inline-flex items-center justify-center gap-2 border border-stone-300 hover:bg-stone-50 rounded-full py-2.5 text-sm font-semibold text-stone-700 transition"
          data-testid="onboarding-retake-btn"
        >
          <RefreshCw className="w-4 h-4" /> Retake
        </button>
        <button
          type="button"
          onClick={onContinue}
          disabled={status !== "ready" || isBlocking}
          className="flex-1 inline-flex items-center justify-center gap-2 font-semibold text-white rounded-full py-2.5 text-sm shadow-md disabled:opacity-50 transition"
          style={{ backgroundColor: KBRJ_TAN }}
          data-testid="onboarding-search-btn"
        >
          <Sparkles className="w-4 h-4" /> Search my photos
        </button>
      </div>
      {isBlocking && (
        <p className="mt-2 text-[11px] text-rose-600 text-center">
          Please retake — we couldn&apos;t detect a face clearly enough to search.
        </p>
      )}
    </div>
  );
}

// -------------- Step 4: results --------------

function ResultStep({ payload, onClose, onDone, coupleName }) {
  // iter88 — Trust EITHER field (count OR array length). `??` alone would
  // return 0 when the backend explicitly says match_count=0 even if the
  // matches array carries entries — that&apos;s the Test-5 stale-count
  // trap. `Math.max` lets a populated array override a stale zero.
  const numericCount = Number(payload?.match_count ?? 0);
  const arrayLength = (payload?.matches?.length ?? payload?.matched_photos?.length ?? payload?.results?.length ?? 0);
  const matchCount = Math.max(numericCount, arrayLength);

  // iter87/88 self-heal: if a race ever lands us on Step 4 with real
  // matches (numeric count > 0 OR non-empty matches array), hand off
  // to the parent gallery immediately so the waitlist card can never
  // flash on top of a successful search.
  useEffect(() => {
    if (matchCount > 0) {
      onDone(payload);
      onClose();
    }
    // Only rerun if the payload identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  const goToPhotos = () => {
    onDone(payload);
    onClose();
  };

  return (
    <div className="p-6 sm:p-7" data-testid="onboarding-step-result">
      <StepHeader step={4} total={4} title={matchCount > 0 ? "Your photos" : "You're all set" } onClose={onClose} />

      {matchCount > 0 ? (
        <div className="mt-4 text-center" data-testid="onboarding-result-matches">
          <div
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-white font-semibold text-sm"
            style={{ backgroundColor: KBRJ_TAN }}
          >
            <Sparkles className="w-4 h-4" strokeWidth={2} />
            We found {matchCount} photo{matchCount === 1 ? "" : "s"} of you
          </div>
          <p className="mt-3 text-sm text-stone-600">
            Jump into the album to see your gallery — we&apos;ve also sent the link to your WhatsApp.
          </p>
          <button
            type="button"
            onClick={goToPhotos}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 font-semibold text-white rounded-full py-3 text-sm shadow-md"
            style={{ backgroundColor: KBRJ_TAN }}
            data-testid="onboarding-view-my-photos"
          >
            View my photos <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="mt-4 text-center" data-testid="onboarding-result-empty">
          <div className="mx-auto w-14 h-14 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
            {payload?.gallery_status === "indexing" ? (
              <Loader2 className="w-7 h-7 text-amber-600 animate-spin" strokeWidth={1.8} />
            ) : (
              <CheckCircle2 className="w-7 h-7 text-amber-600" strokeWidth={1.8} />
            )}
          </div>
          <h4 className="mt-3 font-heading text-lg font-semibold text-stone-900">
            {payload?.gallery_status === "indexing"
              ? "Gallery is still preparing"
              : "You're on the list"}
          </h4>
          <p className="mt-2 text-sm text-stone-600 leading-relaxed" data-testid="onboarding-empty-copy">
            {payload?.gallery_status === "indexing" ? (
              <>
                {payload?.gallery_status_message
                  || "Gallery is still preparing. Please try again shortly."}
                {" "}We'll notify you on WhatsApp the moment it's ready.
              </>
            ) : (
              <>No matches found in {coupleName || "this album"} yet — new photos are added
                through the week. We&apos;ll notify you on WhatsApp the moment your pictures are
                ready.</>
            )}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="mt-5 w-full inline-flex items-center justify-center font-semibold text-white rounded-full py-3 text-sm shadow-md"
            style={{ backgroundColor: KBRJ_TAN }}
            data-testid="onboarding-close-empty"
          >
            Got it
          </button>
        </div>
      )}
    </div>
  );
}

// -------------- Shell --------------

function StepHeader({ step, total, title, onClose, onBack }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.25em] text-rose-700 font-semibold">
        <div className="flex items-center gap-2">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="text-stone-500 hover:text-stone-900 p-1 -ml-1"
              aria-label="Back"
              data-testid={`onboarding-back-step-${step}`}
            >
              <ArrowLeft className="w-3.5 h-3.5" />
            </button>
          )}
          <span>Step {step} of {total}</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-stone-500 hover:text-stone-900 p-1 -mr-1"
          aria-label="Close"
          data-testid={`onboarding-close-step-${step}`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {/* Progress bar */}
      <div className="mt-3 h-1 bg-stone-100 rounded-full overflow-hidden">
        <div
          className="h-full transition-all"
          style={{ width: `${(step / total) * 100}%`, backgroundColor: KBRJ_TAN }}
        />
      </div>
      <h3 className="mt-4 font-heading text-xl font-semibold text-stone-900">{title}</h3>
    </div>
  );
}

export default function GuestOnboardingFlow({
  eventId,
  coupleName = "",
  initialName = "",
  initialPhone = "",
  onDone,
  onClose,
}) {
  const [step, setStep] = useState("intro"); // intro | camera | quality | result
  const [guest, setGuest] = useState({ name: initialName, phone: initialPhone });
  const [capture, setCapture] = useState(null); // { blob, dataUrl }
  const [payload, setPayload] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  // iter88 — race-condition guards.
  //   `inFlightRef`   : swallow duplicate submits (double-click / retry)
  //   `succeededRef`  : once a search returns matches, never allow the
  //                     modal to transition to "result" (waitlist).
  const inFlightRef = useRef(false);
  const succeededRef = useRef(false);

  // iter88 — Single source of truth for "did this response find photos?".
  // Trust EITHER `match_count > 0` OR a non-empty `matches`/`matched_photos`
  // /`results` array so any middlebox that strips one field never causes
  // a valid success to fall through to the waitlist. Coerce to Number so
  // "0" strings from odd proxies are treated as 0.
  const hasSuccessfulMatches = (data) => {
    if (!data || data.error) return false;
    const count = Number(data.match_count ?? 0);
    const arr = data.matches || data.matched_photos || data.results || [];
    return count > 0 || (Array.isArray(arr) && arr.length > 0);
  };

  const runSearch = async () => {
    if (!capture) return;
    if (inFlightRef.current) return; // dedupe rapid re-clicks
    inFlightRef.current = true;
    setSubmitting(true);
    setSubmitError("");
    try {
      // iter72 — reuse the same 1080 px downscale for the search upload
      // so payload + face-match run stay fast even from slow networks.
      const smallBlob = await downscaleBlob(capture.blob, capture.dataUrl, 1080);
      const fd = new FormData();
      fd.append("whatsapp", guest.phone);
      fd.append("name", guest.name);
      fd.append("selfie", smallBlob, "selfie.jpg");
      // iter77 — Cloudflare error on production was hitting the 45 s
      // axios timeout mid-request. Extended to 60 s (matches the backend's
      // 45 s pipeline cap + 15 s headroom for slow mobile networks).
      const { data } = await axios.post(`${API}/public/gallery/${eventId}/find-my-photos`, fd, {
        timeout: 60000,
      });

      // iter88 — SUCCESS PATH takes absolute priority. Any success signal
      // (numeric count OR array length) short-circuits before any state
      // update that could open the waitlist card.
      if (hasSuccessfulMatches(data)) {
        succeededRef.current = true;
        onDone(data);
        onClose();
        return;
      }

      // No matches AND no error — allow the Step 4 waitlist card.
      setPayload(data);
      setStep("result");
    } catch (e) {
      // Backend error — DO NOT show the "no matches" waitlist card. Show
      // an inline error so the guest can retry from Step 3.
      setSubmitError(e.response?.data?.detail || e.message || "Search failed");
    } finally {
      setSubmitting(false);
      inFlightRef.current = false;
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="onboarding-flow-modal">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl relative overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {step === "intro" && (
          <IntroStep
            initialName={guest.name}
            initialPhone={guest.phone}
            onClose={onClose}
            onSubmit={(g) => {
              setGuest(g);
              setStep("camera");
            }}
          />
        )}
        {step === "camera" && (
          <CameraStep
            onClose={onClose}
            onBack={() => setStep("intro")}
            onCapture={(cap) => {
              setCapture(cap);
              setStep("quality");
            }}
          />
        )}
        {step === "quality" && capture && (
          <>
            <QualityStep
              eventId={eventId}
              capture={capture}
              onClose={onClose}
              onBack={() => setStep("camera")}
              onRetake={() => {
                setCapture(null);
                setStep("camera");
              }}
              onContinue={runSearch}
            />
            {(submitting || submitError) && (
              <div className="px-6 sm:px-7 pb-6">
                {submitting && (
                  <div className="flex items-center gap-2 text-xs text-stone-500" data-testid="onboarding-searching">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching the album for your face…
                  </div>
                )}
                {submitError && (
                  <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                    {submitError}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {step === "result" && payload && (
          <ResultStep payload={payload} onClose={onClose} onDone={onDone} coupleName={coupleName} />
        )}
      </div>
    </div>
  );
}

export { clientQuickAnalyse, verdictColor };
